import {
  ExamCenterProctorContentType,
  useGetUploadSignedUrlsForExamCenterProctorLazyQuery,
} from '@exam-center/graphql';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

interface UseUploadThirdProctorFilesInput {
  proctorId?: string;
}

export default function useUploadThirdProctorFiles(
  params?: Readonly<UseUploadThirdProctorFilesInput>,
) {
  const { proctorId = null } = params || {};

  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const [isLoading, setIsLoading] = useState(false);

  const [getUploadSignedUrls, { loading: isUploadSignedUrlLoading }] =
    useGetUploadSignedUrlsForExamCenterProctorLazyQuery();

  const handleUpload = async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return [];
    }

    const contentTypes = files.map<ExamCenterProctorContentType>((file) => {
      switch (file.type) {
        case 'application/pdf':
          return ExamCenterProctorContentType.Pdf;
        case 'image/jpeg':
          return ExamCenterProctorContentType.Jpeg;
        case 'image/png':
          return ExamCenterProctorContentType.Png;
        default:
          throw new Error(`Unsupported file type: ${file.type}`);
      }
    });

    const { data: getUploadSignedUrlsResult } = await getUploadSignedUrls({
      variables: {
        getUploadSignedUrlsForExamCenterProctorInput: {
          examCenterProctorId: proctorId,
          contentTypes,
        },
      },
      fetchPolicy: 'no-cache',
      onError: (error) => {
        throw new Error(error.message);
      },
    });

    if (!getUploadSignedUrlsResult?.ecGetUploadSignedUrlsForExamCenterProctor.signedUrls) {
      throw new Error('Failed to get upload signed URLs.');
    }

    if (
      getUploadSignedUrlsResult.ecGetUploadSignedUrlsForExamCenterProctor.signedUrls.some(
        (url) => !url.storageObject?.fileName,
      )
    ) {
      throw new Error('Some Upload File Names are missing.');
    }

    setIsLoading(true);

    const signedUrlData =
      getUploadSignedUrlsResult.ecGetUploadSignedUrlsForExamCenterProctor.signedUrls;

    try {
      const uploadPromises = files.map(async (file, index) => {
        const {
          url: uploadSignedUrl,
          storageObject: { fileName: uploadFileName },
        } = signedUrlData[index];

        const uploadResponse = await fetch(uploadSignedUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload file: ${file.name}`);
        }

        return {
          originalFileName: file.name,
          uploadFileName: uploadFileName as string,
        };
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      showSnackbar({ variant: 'error', message: 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์' });
      logger.error(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { handleUpload, loading: isUploadSignedUrlLoading || isLoading };
}
